// Pure helpers of Administration → Activité (GET /api/admin/activity): the query of the filters,
// the response in a common shape, page merging, the team of an item, the filter choices, the day
// groups, the labels of an item and the read-only conversation (GET
// /api/admin/activity/chats/:userId/:agentId). No DOM. Tested in test/ui-admin-activity.test.js.
import { t, hasString } from '../../i18n/fr.js';
import { sortedFloors, floorDepartments } from '../../store.js';
import { toDate, dayDiff, formatDate } from '../../format.js';
import { normalizeChatHistory } from '../agent/model.js';
import { sortUsers } from './model.js';
import { formatAuditDetails } from './audit-format.js';
import './strings.js';

/** The kinds of items, as the server names them (the `kinds` filter). */
export const ACTIVITY_KINDS = ['task', 'chat', 'action'];
/** Items asked per page (the server's default; 100 at most). */
export const ACTIVITY_PAGE = 50;
/** Longest preview the server sends (whitespace collapsed, … when cut). */
export const PREVIEW_MAX = 280;
// User ids match [A-Za-z0-9_-], so these select values can never collide with one.
/** Person filter: the members (the server's default, `who=members`). */
export const WHO_MEMBERS = '*members';
/** Person filter: everyone, administrators included (`who=all`). */
export const WHO_ALL = '*all';
/** Where a task item comes from (its source.kind) that has a label. */
const FROMS = ['user', 'chat', 'calendar', 'routine', 'trigger'];
/** Details already shown elsewhere on an action row (its team, its agent). */
const SHOWN_DETAILS = new Set(['floorId', 'deptId', 'agentId']);

function str(v) {
  return typeof v === 'string' ? v : '';
}

function strOrNull(v) {
  return typeof v === 'string' && v ? v : null;
}

/**
 * The query of GET /api/admin/activity for the filters. A chosen person is read with `who=all`
 * (an administrator's own activity is hidden by the default `who=members`); a service only with
 * its floor.
 * @param {{person?: string, floorId?: string, deptId?: string, kind?: string}} [filters]
 * @param {{before?: string|null, limit?: number}} [opts]
 * @returns {Record<string, string|number>}
 */
export function activityQuery({ person = WHO_MEMBERS, floorId = '', deptId = '', kind = '' } = {}, { before = null, limit = ACTIVITY_PAGE } = {}) {
  const q = {};
  if (person === WHO_ALL) q.who = 'all';
  else if (person && person !== WHO_MEMBERS) { q.userId = person; q.who = 'all'; } else q.who = 'members';
  if (floorId) {
    q.floorId = floorId;
    if (deptId) q.deptId = deptId;
  }
  if (ACTIVITY_KINDS.includes(kind)) q.kinds = kind;
  if (before) q.before = String(before);
  if (limit) q.limit = limit;
  return q;
}

/**
 * One item of the response in a common shape (null when unreadable: unknown kind, no id).
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeItem(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!ACTIVITY_KINDS.includes(raw.kind) || typeof raw.id !== 'string' || !raw.id) return null;
  const item = {
    id: raw.id,
    kind: raw.kind,
    at: str(raw.at),
    userId: str(raw.userId),
    userName: str(raw.userName),
    role: raw.role === 'admin' || raw.role === 'member' ? raw.role : null,
    floorId: strOrNull(raw.floorId),
    deptId: strOrNull(raw.deptId),
    agentId: strOrNull(raw.agentId),
    agentName: strOrNull(raw.agentName),
  };
  if (raw.kind === 'task') {
    Object.assign(item, {
      taskId: str(raw.taskId), title: str(raw.title), preview: str(raw.preview), state: str(raw.state), from: str(raw.from),
    });
  } else if (raw.kind === 'chat') {
    const conv = raw.conversation && typeof raw.conversation === 'object' ? raw.conversation : {};
    Object.assign(item, {
      preview: str(raw.preview),
      files: Number.isInteger(raw.files) && raw.files > 0 ? raw.files : 0,
      conversation: { userId: str(conv.userId) || item.userId, agentId: str(conv.agentId) || item.agentId || '' },
    });
  } else {
    Object.assign(item, {
      action: str(raw.action),
      target: raw.target === null || raw.target === undefined ? null : String(raw.target),
      details: raw.details ?? null,
    });
  }
  return item;
}

/**
 * The response of GET /api/admin/activity in a common shape.
 * @param {unknown} res {items, nextBefore, chats}
 * @returns {{items: object[], nextBefore: string|null, chats: boolean}}
 */
export function normalizeActivity(res) {
  const list = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
  return {
    items: list.map(normalizeItem).filter(Boolean),
    nextBefore: typeof res?.nextBefore === 'string' && res.nextBefore ? res.nextBefore : null,
    chats: res?.chats === true,
  };
}

/**
 * The items shown after one more page: the older page appended, an item already shown (same id)
 * kept once.
 * @param {object[]} shown
 * @param {object[]} page
 */
export function mergeItems(shown, page) {
  const seen = new Set((shown || []).map((it) => it.id));
  const out = [...(shown || [])];
  for (const it of page || []) {
    if (!it || seen.has(it.id)) continue;
    seen.add(it.id);
    out.push(it);
  }
  return out;
}

/**
 * The names of an item's floor and service in the office as it is now ('' when unknown).
 * @param {{floorId?: string|null, deptId?: string|null}} item
 * @param {{floors?: object[], departments?: object[]}} [office]
 * @returns {{floor: string, dept: string}}
 */
export function teamParts(item, office = {}) {
  const floor = item?.floorId ? (office.floors || []).find((f) => f.id === item.floorId) : null;
  const dept = floor && item?.deptId ? (office.departments || []).find((d) => d.id === item.deptId && d.floorId === floor.id) : null;
  return { floor: str(floor?.name), dept: str(dept?.name) };
}

/** « Étage · Service », « Étage », or « — » when the item has no (known) team. */
export function teamLabel(item, office = {}) {
  const { floor, dept } = teamParts(item, office);
  return [floor, dept].filter(Boolean).join(' · ') || t('adm.activity.noTeam');
}

/** A person in the filter: the display name, « (administrateur) » after an administrator's. */
export function personLabel(user) {
  const name = str(user?.displayName) || str(user?.username) || str(user?.id);
  return user?.role === 'admin' ? t('adm.activity.adminName', { name }) : name;
}

/**
 * Choices of the « Personne » filter: the members (default), everyone, then each account
 * (sortUsers: administrators first, then by name). A chosen person missing from the list (deleted
 * since, or the list did not load) is kept under `currentName`.
 * @param {object[]} users GET /api/users
 * @param {{current?: string, currentName?: string}} [opts]
 * @returns {Array<{value: string, label: string}>}
 */
export function personOptions(users, { current = '', currentName = '' } = {}) {
  const out = [{ value: WHO_MEMBERS, label: t('adm.activity.who.members') }, { value: WHO_ALL, label: t('adm.activity.who.all') }];
  for (const u of sortUsers(users)) if (u && typeof u.id === 'string' && u.id) out.push({ value: u.id, label: personLabel(u) });
  if (current && !out.some((o) => o.value === current)) out.push({ value: current, label: currentName || current });
  return out;
}

/** Choices of the « Étage » filter: every floor, then each floor in the office's order. */
export function floorChoices(office = {}) {
  return [{ value: '', label: t('adm.activity.allFloors') },
    ...sortedFloors(office.floors || []).map((f) => ({ value: f.id, label: str(f.name) || f.id }))];
}

/** Choices of the « Service » filter for a floor (only « Tous les services » without one). */
export function deptChoices(office = {}, floorId = '') {
  return [{ value: '', label: t('adm.activity.allDepts') },
    ...(floorId ? floorDepartments(office.departments || [], floorId).map((d) => ({ value: d.id, label: str(d.name) || d.id })) : [])];
}

/** Choices of the « Type » filter; « Discussions » only when the server lets administrators read them. */
export function kindChoices(chats = false) {
  return [{ value: '', label: t('adm.activity.kind.all') },
    ...ACTIVITY_KINDS.filter((k) => k !== 'chat' || chats).map((k) => ({ value: k, label: t(`adm.activity.kind.${k}`) }))];
}

/**
 * The filters checked against the office: an unknown floor drops the floor and the service, a
 * service of another floor drops the service, an unknown type goes back to « Tout ».
 * @param {{person?: string, floorId?: string, deptId?: string, kind?: string}} filters
 * @param {object} office
 */
export function cleanFilters(filters = {}, office = {}) {
  const person = str(filters.person) || WHO_MEMBERS;
  let floorId = str(filters.floorId);
  let deptId = str(filters.deptId);
  if (floorId && !(office.floors || []).some((f) => f.id === floorId)) { floorId = ''; deptId = ''; }
  if (deptId && !(office.departments || []).some((d) => d.id === deptId && d.floorId === floorId)) deptId = '';
  const kind = ACTIVITY_KINDS.includes(filters.kind) ? filters.kind : '';
  return { person, floorId, deptId, kind };
}

/**
 * The label of a day header: « Aujourd’hui », « Hier », else « lundi 12 octobre ».
 * @param {*} at
 * @param {number} [now]
 */
export function dayLabel(at, now = Date.now()) {
  const d = toDate(at);
  if (!d) return t('adm.activity.unknownDay');
  const diff = dayDiff(d, now);
  if (diff === 0) return t('adm.activity.today');
  if (diff === 1) return t('adm.activity.yesterday');
  return formatDate(d, { now, weekday: true, long: true });
}

/**
 * Items grouped by day, in their order (newest first): [{key, label, items}].
 * @param {object[]} items
 * @param {number} [now]
 */
export function groupByDay(items, now = Date.now()) {
  const groups = [];
  for (const it of items || []) {
    const d = toDate(it?.at);
    const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '?';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(it);
    else groups.push({ key, label: dayLabel(it?.at, now), items: [it] });
  }
  return groups;
}

/** Where a request comes from (« Depuis une discussion »…), or '' for an unknown source. */
export function fromLabel(from) {
  return FROMS.includes(from) ? t(`adm.activity.from.${from}`) : '';
}

/** True when the task state has a label (its badge would otherwise show a raw key). */
export function knownState(state) {
  return typeof state === 'string' && state !== '' && hasString(`state.${state}`);
}

/** « Discussion avec REDAC » (a deleted agent: « avec un agent supprimé »). */
export function chatTitle(item) {
  return t('adm.activity.chatWith', { agent: item?.agentName || t('adm.activity.goneAgent') });
}

/** The details of an action row, without what the row already shows (team, agent). */
export function actionDetails(item, max = 180) {
  const d = item?.details;
  if (!d || typeof d !== 'object' || Array.isArray(d)) return formatAuditDetails(d, max);
  return formatAuditDetails(Object.fromEntries(Object.entries(d).filter(([k]) => !SHOWN_DETAILS.has(k))), max);
}

/** The read-only conversation of a person with an agent (administrators only). */
export function adminChatPath(userId, agentId) {
  return `/api/admin/activity/chats/${encodeURIComponent(userId)}/${encodeURIComponent(agentId)}`;
}

/** One image of that conversation. */
export function adminChatFileUrl(userId, agentId, name) {
  return `${adminChatPath(userId, agentId)}/files/${encodeURIComponent(name)}`;
}

/**
 * The response of the conversation route in a common shape; its messages as the person's own
 * history (GET /api/agents/:id/chat) is read.
 * @param {unknown} res {userId, userName, role, agentId, agentName, floorId, deptId, messages}
 * @param {{userId?: string, agentId?: string, userName?: string, agentName?: string}} [fallback]
 */
export function normalizeConversation(res, fallback = {}) {
  const r = res && typeof res === 'object' ? res : {};
  return {
    userId: str(r.userId) || str(fallback.userId),
    userName: str(r.userName) || str(fallback.userName),
    role: r.role === 'admin' || r.role === 'member' ? r.role : null,
    agentId: str(r.agentId) || str(fallback.agentId),
    agentName: str(r.agentName) || str(fallback.agentName),
    floorId: strOrNull(r.floorId),
    deptId: strOrNull(r.deptId),
    messages: normalizeChatHistory(r),
  };
}
